import { IGif } from "services/types";
import { boostHistory } from "./boostHistory";

interface IMixWithBoostedProps {
  // The contents that we inject into.
  gifs: IGif[];

  // Gifs to be injected.
  boostedPostData: IGif[];

  // True if we're in tiles mode, showing no boosted gifs.
  tiles: boolean;

  // page number, prevent removing boosted gifs on pagination
  page?: number;
}

interface IBoostedGifsPos {
  id: string;
  position: number;
}

interface IBoostSpread {
  [id: string]: string;
}

const DEFAULT_POSITION = 5;

export const spreadBoosted = (sources: IBoostedGifsPos[]): IBoostSpread => {
  const res: IBoostSpread = {};

  sources.forEach((boost: IBoostedGifsPos) => {
    for (let pos = boost.position; ; pos += boost.position) {
      if (!(pos in res)) {
        res[pos] = boost.id;
        break;
      }
    }
  });

  return res;
};

export const mixWithBoosted = ({ gifs, boostedPostData, tiles, page }: IMixWithBoostedProps): IGif[] => {
  if (tiles) {
    const pureGifs = gifs.filter(
      gif => !boostedPostData.map((post: IGif) => post.id).includes(gif.id)
    );

    return pureGifs;
  }

  // IDs of boosted gifs.
  const boostedGifIds = new Set(boostedPostData.map(gif => gif.id));

  // Filter out non-boosted gifs that have a corresponding boosted version in the feed.
  const uniqueGifs = gifs.filter(gif => !boostedGifIds.has(gif.id));

  // Get ids of boosted gifs already seen (to hide).
  const notExpiredBoostGifs = page === 1 ? boostHistory.getCurrent() : [];

  // Get bodies of boosted gifs that weren't seen.
  const newBoostedPost = boostedPostData.filter(gif => !notExpiredBoostGifs.includes(gif.id));

  const adaptBoosted = newBoostedPost.map(gif => ({ id: gif.id, position: gif?.cta?.position || DEFAULT_POSITION }));

  const positions = spreadBoosted(adaptBoosted);

  const sortedPositions = Object.keys(positions)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .reduce((result: { [key: string]: string }, key) => {
      result[key] = positions[key];
      return result;
    }, {});

  const newArr: IGif[] = [];

  for (let i = 0; i < uniqueGifs.length; i++) {
    newArr.push(uniqueGifs[i]);

    // Check if a boosted gif should be inserted after the current position
    if (sortedPositions[i + 1]) {
      const boostedGif = newBoostedPost.find(el => el.id === sortedPositions[i + 1]);

      if (boostedGif) {
        newArr.push(boostedGif);
      }
    }
  }

  return newArr;
};
